import { InfoCircleFilled } from "@ant-design/icons";
import { Alert, Button, Empty, Tooltip } from "antd";
import { showError } from "common/ToastNotifications";
import { previewGateNotice } from "common/reviewQueue";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import LoadingOverlay from "components/atoms/LoadingOverlay";
import BasicFormBuilder from "components/molecules/BasicFormBuilder";
import { Component } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { Action, Dispatch } from "redux";
import actions from "store/actions";
import AppState from "store/AppState";

const mapStateToProps = (state: AppState) => {
    return {
        getNominatedDataObjectMergePreviewResult: state.dataObjectDuplicateMergePreview.getNominatedDataObjectMergePreviewResult,
        nominatedDataObjectMergePreview: state.dataObjectDuplicateMergePreview.nominatedDataObjectMergePreview,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action<string>>) => {
    return {
        getNominatedDataObjectMergePreview: (duplicateSetId: string, duplicateSetVersion: string, groupId: string) =>
            dispatch(actions.getNominatedDataObjectMergePreviewRequest(duplicateSetId, duplicateSetVersion, groupId)),
    };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type IPropsFromRedux = ConnectedProps<typeof connector>;

interface IDataObjectDuplicateMergePreviewProps extends IPropsFromRedux {
    duplicateSetId: string;
    duplicateSetVersion: string | null;
    groupId: string | null;
    onClose: () => void;
}

interface IDataObjectDuplicateMergePreviewStates {
    fetchingPreview: boolean;
    showPreview: boolean;
    busy: boolean;
    busyMessage: string;
}

class DataObjectDuplicateMergePreview extends Component<IDataObjectDuplicateMergePreviewProps & RouteComponentProps, IDataObjectDuplicateMergePreviewStates> {
    constructor(props: IDataObjectDuplicateMergePreviewProps & RouteComponentProps) {
        super(props);
        this.state = {
            fetchingPreview: false,
            showPreview: true,
            busy: false,
            busyMessage: "Please wait...",
        };
    }

    componentWillMount() {}

    componentDidMount() {
        this.setState({
            fetchingPreview: true,
        });
        if (this.props.duplicateSetVersion && this.props.groupId) {
            this.props.getNominatedDataObjectMergePreview(this.props.duplicateSetId, this.props.duplicateSetVersion, this.props.groupId);
        }
    }

    componentDidUpdate(prevProps: IDataObjectDuplicateMergePreviewProps & RouteComponentProps) {
        this.onGetNominatedDataObjectMergePreviewResultChanged(prevProps);
    }

    onGetNominatedDataObjectMergePreviewResultChanged = (prevProps: IDataObjectDuplicateMergePreviewProps & RouteComponentProps) => {
        if (this.props.getNominatedDataObjectMergePreviewResult && this.props.getNominatedDataObjectMergePreviewResult !== prevProps.getNominatedDataObjectMergePreviewResult) {
            if (!this.props.getNominatedDataObjectMergePreviewResult.success) {
                showError("Unable to generate preview at the moment");
            }
            this.setState({
                fetchingPreview: false,
            });
        }
    };

    isBusy = () => {
        let isBusy = this.state.busy || this.state.fetchingPreview;
        return isBusy;
    };

    onCancelClick = () => {
        this.setState({
            showPreview: false,
        });
        this.props.onClose();
    };

    render() {
        const preview = this.props.nominatedDataObjectMergePreview;
        const gateNotice = preview.record ? previewGateNotice(preview.gate || null, preview.schema?.fields || []) : null;
        return (
            <>
                {this.isBusy() ? (
                    <FlexContainerLayout contentClassName="overflow-hidden">
                        <LoadingOverlay busy={this.isBusy()} spinner message={this.state.busyMessage || "Please wait..."}></LoadingOverlay>
                    </FlexContainerLayout>
                ) : (
                    <FlexContainerLayout
                        containerClassName="gap-0"
                        headerClassName="w-full p-4 pb-2 text-xl font-bold gap-2"
                        header={
                            <>
                                <h3>Merge Preview</h3>
                                <Tooltip title={`Duplicate Set ID: ${this.props.duplicateSetId}`} destroyOnHidden>
                                    <InfoCircleFilled className="text-slate-400" />
                                </Tooltip>
                            </>
                        }
                        footerClassName="bg-slate-100 px-4 py-3 justify-end"
                        footer={<Button onClick={this.onCancelClick}>Close</Button>}
                    >
                        {gateNotice && <Alert className="!mx-4 !mt-2" type={gateNotice.type} showIcon title={gateNotice.text} data-testid="merge-preview-gate-notice" />}
                        {this.props.nominatedDataObjectMergePreview.record ? (
                            <BasicFormBuilder
                                showHeader={false}
                                showFooter={false}
                                key={this.props.nominatedDataObjectMergePreview.record.dataObjectId}
                                formDataObject={this.props.nominatedDataObjectMergePreview.record}
                                schemaFields={this.props.nominatedDataObjectMergePreview.schema.fields}
                                readOnly={true}
                                contentClassName="!pl-4 !pr-0 !pt-2"
                            ></BasicFormBuilder>
                        ) : (
                            <Empty description="No preview found" />
                        )}
                    </FlexContainerLayout>
                )}
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(DataObjectDuplicateMergePreview));
